import React, { useState } from "react";
import {
  Camera,
  File,
  Loader2,
  Paperclip,
  Plus,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  attachmentUploadForm,
  containerLineDriverAPI,
} from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_FILES_PER_ZONE,
  ATTACHMENT_MAX_FILE_SIZE_MB,
} from "../../constants/containerOrderConstants";
import {
  canRemoveAttachment,
  deliverAttachment,
  screenAttachments,
  uploaderName,
} from "../../utils/containerAttachments";
import type {
  AttachmentCategory,
  ContainerLineAttachment,
} from "../../types/containerOrders";
import { DriverSection } from "./DriverSection";

interface DriverLineAttachmentsProps {
  lineId: string;
  attachments: ContainerLineAttachment[];
  /** Replaces the whole list with what the server just returned. */
  /**
   * The new document list, and the container's new version token with it.
   *
   * The token is not optional decoration. Filing or removing a document is a
   * save, so it moves `updatedAt`, and a screen still holding the token it
   * loaded with would have its *next* save refused as a lost update — telling
   * the driver somebody else had changed the container when the only somebody
   * was them, one tap earlier.
   */
  onChange: (
    attachments: ContainerLineAttachment[],
    updatedAt?: string,
  ) => void;
  /** The signed-in driver, for deciding which rows are theirs. */
  userId?: string;
  /** A cancelled container: still readable, no longer added to. */
  readonly?: boolean;
  /**
   * Where this list is being drawn.
   *
   * `inline` puts it inside the stop frame, where it belongs while there is a
   * stop being worked: the paper is handed over at the gate, in the same minute
   * as the confirmation, and a driver should not have to leave the card they
   * are working to file it. `card` is the standalone collapsible block, which
   * is what is left once the run is finished and there is no frame to sit in.
   *
   * The difference is the wrapper and nothing else. One implementation of
   * uploading, removing and permissions, because two would be two chances for
   * the delete rule to drift.
   */
  variant?: "card" | "inline";
  /**
   * A tile pinned before the filed documents.
   *
   * The container's CMR goes here. It is drawn from the container rather than
   * stored against it — nothing is uploaded, nothing can be deleted — so it is
   * not an attachment and must not be mixed into the list as though it were.
   * But it *is* one of the papers this box travels with, and the strip is where
   * a driver looks for those, so it belongs in the strip and nowhere else.
   *
   * Passed in rather than built here for the same reason `documents` is passed
   * into the stop frame: this component knows about uploading and permissions,
   * and knows nothing about consignment notes.
   */
  leading?: React.ReactNode;
}

/**
 * The container's documents, from the yard.
 *
 * The same two zones the office uses, the same limits, and the same one list —
 * a driver opening this sees the booking paperwork dispatch filed, and dispatch
 * sees the gate receipt photographed here. What differs is when the file goes
 * up. The dispatcher's page holds picked files until Save, because they are part
 * of an edit that may still be abandoned; this screen has no such save, and a
 * receipt photographed at the gate wants to be filed at the gate, on the spot,
 * before the phone goes back in a pocket. So a pick uploads immediately.
 *
 * Removal is limited to the driver's own uploads. The office's documents are
 * shown with a badge and no bin — the API refuses those regardless, but a button
 * that only exists where it works is a better explanation than an error.
 */
export function DriverLineAttachments({
  lineId,
  attachments,
  onChange,
  userId,
  readonly = false,
  variant = "card",
  leading,
}: DriverLineAttachmentsProps) {
  const { t } = useTranslation("containerOrders");
  const { confirm, confirmDialog } = useConfirm();
  const [uploading, setUploading] = useState<AttachmentCategory | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const viewer = { userId, isDriver: true };

  const upload = async (category: AttachmentCategory, selected: File[]) => {
    const { kept, rejected } = screenAttachments(
      selected,
      ATTACHMENT_MAX_FILES_PER_ZONE,
    );

    for (const rejection of rejected) {
      toast.error(
        rejection.reason === "type"
          ? t("attachments.rejectedType", { name: rejection.name })
          : rejection.reason === "size"
            ? t("attachments.rejectedSize", {
                name: rejection.name,
                limit: ATTACHMENT_MAX_FILE_SIZE_MB,
              })
            : t("attachments.rejectedCount", {
                name: rejection.name,
                limit: ATTACHMENT_MAX_FILES_PER_ZONE,
              }),
      );
    }

    if (!kept.length) return;

    setUploading(category);
    try {
      const res = await containerLineDriverAPI.uploadAttachments(
        lineId,
        attachmentUploadForm(
          category === "customs"
            ? { customsAttachments: kept }
            : { attachments: kept },
        ),
      );
      // The whole list comes back, so this also picks up anything the office
      // filed since the screen was opened.
      onChange(
        res.data.data.attachments as ContainerLineAttachment[],
        res.data.data.updatedAt as string | undefined,
      );
      toast.success(t("lineAttachments.uploaded"));
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || t("lineAttachments.uploadFailed"));
    } finally {
      setUploading(null);
    }
  };

  const download = async (attachment: ContainerLineAttachment) => {
    try {
      setBusyId(attachment._id);
      const res = await containerLineDriverAPI.downloadAttachment(
        lineId,
        attachment._id,
      );
      deliverAttachment(res, attachment);
    } catch {
      toast.error(t("attachments.downloadFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (attachment: ContainerLineAttachment) => {
    // Asked about on this screen most of all: it is a phone, the bin sits a
    // thumb's width from the download button, and the file is gone from disk
    // the moment it is pressed. The dialog rises from the bottom edge here, so
    // both answers are under the thumb that caused the question.
    const agreed = await confirm({
      title: t("confirm.titles.deleteAttachment"),
      description: t("lineAttachments.confirmDelete", {
        name: attachment.originalName,
      }),
      confirmLabel: t("confirm.actions.deleteAttachment"),
      cancelLabel: t("confirm.actions.keep"),
      tone: "danger",
      // The driver's screen is capped at phone width, and a dialog wider than
      // the column it rises over reads as belonging to a different app.
      maxWidthClass: "max-w-md",
    });
    if (!agreed) return;

    try {
      setBusyId(attachment._id);
      const res = await containerLineDriverAPI.deleteAttachment(
        lineId,
        attachment._id,
      );
      onChange(
        attachments.filter((a) => a._id !== attachment._id),
        res?.data?.data?.updatedAt as string | undefined,
      );
      toast.success(t("attachments.deleted"));
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || t("attachments.deleteFailed"));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * One of the two things a driver can add, as a button that says what it is.
   *
   * The wording is the point. These were labelled "General attachments" and
   * "Customs documents", which are the office's filing categories — and the
   * person tapping them is standing at a gate holding a piece of paper somebody
   * has just handed them. They do not have a category in mind; they have a
   * *paper* in their hand. So the buttons name the paper: the one that travels
   * with the load, and the one customs wants. Same two categories underneath,
   * asked in the language of the yard rather than of the filing cabinet.
   *
   * The third stored category — `plate` — has no button here on purpose. It is
   * not paperwork anybody chooses to file; it is the photograph of the door the
   * container's weights were read from, written by the plate import in one act
   * with the figures it produced. A button would invite a photograph filed as
   * evidence for figures nobody saved.
   */
  const addButton = (category: AttachmentCategory) => {
    const isCustoms = category === "customs";
    const busy = uploading === category;

    return (
      <label
        key={category}
        /**
         * Boxed, not a pill — 10px, the same corner the filed thumbnails use.
         *
         * A pill is the shape of an *action* on this screen: Navigate, I'm
         * here, Door photo, Edit are all pills, and these two were wearing that
         * shape while sitting in a strip of 10px boxes. So they read as buttons
         * belonging to the card above rather than as the empty slots of the row
         * they are actually part of.
         *
         * Squaring them puts them in the same family as the documents they
         * produce: a filed document and the place you file one are the same
         * object in two states, and the dashed edge is the whole of the
         * difference. That is the reference sheet's own add tile, widened to
         * carry a label.
         */
        className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-dashed px-2 py-3 text-center ${
          isCustoms
            ? "border-[#EF9F27] bg-[#FAEEDA] text-[#854F0B]"
            : "border-[#C9C7BE] bg-[#F7F6F2] text-[#6B6A64]"
        } ${readonly || uploading ? "pointer-events-none opacity-60" : ""}`}
      >
        {/* The plus in a filled circle, leading. It is the whole affordance:
            the icons that were here — a camera and a shield — named the *kind*
            of paper and left "press this to add one" to be inferred from an
            outline. A driver read them as headings. */}
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            isCustoms ? "bg-[#EF9F27] text-white" : "bg-[#C9C7BE] text-white"
          }`}
          aria-hidden
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" strokeWidth={3} />
          )}
        </span>
        <span className="text-[12px] font-semibold leading-tight">
          {t(isCustoms ? "driver.addCustomsPaper" : "driver.addTransportPaper")}
        </span>
        {/*
          `capture` is deliberately absent. It would force the camera and skip
          the gallery, and a driver photographing four pages of a declaration
          takes them first and files them second — often after walking back to
          the cab. Offering both is what the phone's own picker already does.
        */}
        <input
          type="file"
          multiple
          accept={ATTACHMENT_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",")}
          className="hidden"
          disabled={readonly || !!uploading}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const selected = Array.from(event.target.files || []);
            event.target.value = "";
            if (selected.length) upload(category, selected);
          }}
        />
      </label>
    );
  };

  /**
   * What is already filed, as a row of tiles.
   *
   * Tiles rather than a list of filenames. A row per document is the right
   * shape at a desk, where the name is what is being scanned for; on a phone it
   * is a column of truncated strings, and what the driver is looking for is
   * "the customs one" or "the photo I took at the gate" — a colour and a shape,
   * not a name.
   *
   * The tiles carry a type mark and a caption, not a rendered preview. Every
   * document here sits behind an authenticated download, so previews would mean
   * one authenticated request per tile, on a phone at a terminal — a lot of
   * bytes over a bad connection to decorate a list somebody is about to tap
   * once. The tap fetches the one they wanted.
   */
  const tiles = (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {leading}
      {attachments.map((attachment) => {
        const fromDriver = attachment.uploadedByRole === "driver";
        const isPlate = attachment.category === "plate";
        const isCustoms = attachment.category === "customs";
        const mine = canRemoveAttachment(attachment, viewer);

        return (
          <div key={attachment._id} className="relative shrink-0 pt-1.5 pr-1.5">
            <button
              type="button"
              disabled={busyId === attachment._id}
              onClick={() => download(attachment)}
              title={`${attachment.originalName} · ${
                uploaderName(attachment) || t("lineAttachments.unknownUploader")
              }`}
              className={`flex h-[60px] w-[60px] flex-col items-center justify-center gap-0.5 rounded-[10px] border ${
                isPlate
                  ? "border-[#EF9F27] bg-[#FAEEDA]"
                  : isCustoms
                    ? "border-[#EF9F27] bg-[#FAEEDA]"
                    : "border-[#C9C7BE] bg-[#F7F6F2]"
              }`}
            >
              {busyId === attachment._id ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#A6A49C]" />
              ) : isPlate ? (
                <Camera className="h-4 w-4 text-[#854F0B]" />
              ) : (
                <File
                  className={`h-4 w-4 ${
                    isCustoms ? "text-[#854F0B]" : "text-[#6B6A64]"
                  }`}
                />
              )}
              {/* What kind of paper this is, in the same words the button that
                  filed it used. The filename is on the tile's title and comes
                  back intact with the download — it is almost never what a
                  driver identifies a document by, and at this size it is eight
                  characters of "IMG_20240..." either way. */}
              <span className="w-full truncate px-1 text-[8.5px] font-semibold leading-tight text-[#6B6A64]">
                {t(
                  isPlate
                    ? "driver.docPlate"
                    : isCustoms
                      ? "driver.docCustoms"
                      : "driver.docTransport",
                )}
              </span>
              {/* Which side filed it. The office's documents cannot be removed
                  here, and this is what says so before the missing bin has to
                  be noticed. */}
              <span
                className={`rounded px-1 text-[7.5px] font-bold uppercase leading-tight ${
                  fromDriver
                    ? "bg-[#EF9F27] text-white"
                    : "bg-[#E4E2DA] text-[#6B6A64]"
                }`}
              >
                {t(
                  fromDriver
                    ? "lineAttachments.byDriver"
                    : "lineAttachments.byOffice",
                )}
              </span>
            </button>

            {/* Only the driver's own. The API refuses the rest regardless, and a
                button that exists only where it works is a better explanation
                than an error. Cornered rather than placed inline, so it is not a
                thumb's width from the tile's own tap target — which on a phone
                is how a document gets deleted instead of opened. */}
            {!readonly && mine && (
              <button
                type="button"
                disabled={busyId === attachment._id}
                onClick={() => remove(attachment)}
                aria-label={t("attachments.delete")}
                className="absolute right-0 top-0 rounded-full bg-white p-1 text-[#A63A39] shadow-sm ring-1 ring-[#E4E2DA]"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  /**
   * The block itself, wrapper aside.
   *
   * Ordered the way it is used: what to do, then what has already been done.
   * The buttons lead because filing is the reason this is on screen at a gate —
   * the driver is holding a piece of paper — and the tiles below are the
   * receipt for having done it. The old order put an empty grid first and the
   * action underneath it, which on a container with no documents yet meant the
   * first thing on the block was a blank space.
   */
  const content = (
    <>
      {!readonly && (
        <div className="mb-2 flex gap-2">
          {addButton("general")}
          {addButton("customs")}
        </div>
      )}

      {/* The strip renders for a pinned tile alone — the CMR is always there,
          so a container with no uploads is no longer an empty space. The hint
          still shows underneath in that case rather than instead: "nothing has
          been filed yet" and "photograph anything you are handed" are two
          different things to say, and the second is the useful one. */}
      {(leading || attachments.length > 0) && tiles}

      {attachments.length === 0 && (
        <p className="rounded-[10px] bg-[#F7F6F2] px-3 py-2.5 text-center text-[11px] leading-snug text-[#A6A49C]">
          {t(readonly ? "lineAttachments.empty" : "driver.docsEmptyHint")}
        </p>
      )}

      {confirmDialog}
    </>
  );

  // Inside the stop frame: no card of its own, since it is already inside one,
  // and a heading in the frame's own register rather than the shouty uppercase
  // the standalone block uses.
  if (variant === "inline") {
    return (
      <div className="rounded-[10px] bg-white p-3">
        <p className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-[#6B6A64]">
          <Paperclip className="h-3.5 w-3.5 shrink-0" />
          {t("driver.documents")}
          {attachments.length > 0 && (
            <span className="font-normal text-[#A6A49C]">
              · {t("driver.documentsCount", { count: attachments.length })}
            </span>
          )}
        </p>
        {content}
      </div>
    );
  }

  /* Standalone, for a finished run where there is no frame to sit in. Folded by
     default: at that point the list is consulted rather than added to. */
  return (
    <DriverSection
      title={t("lineAttachments.title")}
      icon={Paperclip}
      summary={t("lineAttachments.driverSummary", {
        count: attachments.length,
      })}
    >
      {content}
    </DriverSection>
  );
}

export default DriverLineAttachments;
